import { Col, Row } from 'antd';
import { useCallback } from 'react';
import { addDays } from 'date-fns/addDays';
import { format as formatGregorian } from 'date-fns/format';
import { isValid } from 'date-fns/isValid';
import { parse } from 'date-fns/parse';
import { format as formatJalali } from 'date-fns-jalali/format';
import { startOfWeek } from 'date-fns-jalali/startOfWeek';
import { faIR } from 'date-fns-jalali/locale';
import { MinusCircleOutlined } from '@ant-design/icons';
import { Flex } from '../../common';
import { useSelfContext } from '@/context';
import { ReserveInfoModalData } from '../../modals/ReserveInfoModal';
import { FaCheckCircle } from 'react-icons/fa';
import classNames from 'classnames';

export interface ReserveReportTableProps {
  reports?: ReportDayEntity[];
  mealTypes?: ReserveMealEntity[];
  onItemClick?: (data: ReserveInfoModalData) => any;
}

function weekStartFromShowingDate(showingDate?: string) {
  const datePart = showingDate?.trim().split('+')[0];
  const parsed = datePart ? parse(datePart, 'yyyy-MM-dd', new Date()) : new Date();
  return startOfWeek(isValid(parsed) ? parsed : new Date());
}

export function ReserveReportMobile({
  reports = [],
  mealTypes,
  onItemClick
}: ReserveReportTableProps) {

  const { showingDate } = useSelfContext();
  const startDate = weekStartFromShowingDate(showingDate);
  const dates = Array.from({ length: 7 }, (_, index) => addDays(startDate, index));

  const len = mealTypes?.length ?? 1;
  const colWidth = `${100 / (len)}%`;
  const colMinWidth = '60px';

  const renderReportStatus = useCallback((date: Date, meal: ReserveMealEntity) => {
    const dateKey = formatGregorian(date, 'yyyy-MM-dd');
    const filteredReports = reports.filter(report => report.date?.slice(0, 10) === dateKey);
    for (const item of filteredReports) {
      const filteredMealTypes = (item.mealTypes ?? []).filter(mealType => (
        mealType.reserve != null ||
        mealType.teammateReserve != null
      ));

      const dailysales = (item.dailySales ?? []).filter(sale => (
        sale?.mealTypeId == meal.id
      ));

      const hasReserves = filteredMealTypes.some(mealType => meal.id === mealType.mealTypeId);
      const hasDailySales = dailysales.length > 0;

      if (hasReserves || hasDailySales) {
        const foods = filteredMealTypes.filter(mealType => mealType.mealTypeId === meal.id);

        const reserveConsumedCount = foods.filter(food => (
          food?.reserve?.consumed ||
          food?.teammateReserve?.consumed
        )).length;
        const dailySaleConsumedCount = dailysales.filter(sale => sale?.consumed).length;
        const totalItems = foods.length + dailysales.length;
        const isAllConsumed = (reserveConsumedCount + dailySaleConsumedCount) === totalItems;

        return (
          <FaCheckCircle
            className={classNames('text-[1.25rem]', isAllConsumed ? 'text-accent' : 'text-primary')}
            onClick={() => onItemClick({
              meal,
              report: item,
              mealTypes: foods,
              startServeTime: foods[0]?.startServeTime || foods[0]?.teammateReserve?.startServeTime,
              endServeTime: foods[0]?.endServeTime || foods[0]?.teammateReserve?.endServeTime,
              demonstrationDescription: foods[0]?.demonstrationDescription || foods[0]?.teammateReserve?.demonstrationDescription
            })}
          />
        );
      }
    }
    return <MinusCircleOutlined className="text-[1.25rem] text-gray-400" />;
  }, [reports, onItemClick]);

  return (
    <Flex className="app-reserve-report-mobile h-full" align="flex-start" justify="center">
      <Row className="w-full h-full">
        <Col className="ps-4 py-4 h-full" span={7}>
          <Flex className="date-sidebar" direction="column" justify="center">
            <Flex className="date-text">{formatJalali(startDate, 'yyyy/MM/dd')}</Flex>
            {dates.map((date, index) => (
              <Flex className="date-sidebar-item" key={index}>
                <span>{formatJalali(date, 'EEEE', { locale: faIR })}</span>
              </Flex>
            ))}
            <Flex className="date-text">
              {formatJalali(addDays(startDate, 6), 'yyyy/MM/dd')}
            </Flex>
          </Flex>
        </Col>
        <Col span={17} className="h-full w-full py-4 pe-4">
          <Flex className="meals-status" direction="column" align="flex-start" justify="center">

            <Flex className="status-item !bg-[#E5E5E5]" align="center">
              {(mealTypes ?? []).map((meal, index) => (
                <Flex
                  key={index}
                  className="status-col"
                  style={{ width: colWidth, minWidth: colMinWidth }}
                >
                  {meal.name}
                </Flex>
              ))}
            </Flex>

            {dates.map((date, index) => (
              <Flex key={index} className="status-item" align="center">
                {(mealTypes ?? []).map((meal, mealIndex) => (
                  <Flex
                    key={mealIndex}
                    className="status-col"
                    style={{ width: colWidth, minWidth: colMinWidth }}
                  >
                    {renderReportStatus(date, meal)}
                  </Flex>
                ))}
              </Flex>
            ))}
            <Flex className="status-item !bg-transparent" align="center"></Flex>
          </Flex>
        </Col>
      </Row>
    </Flex>
  );
}

export default ReserveReportMobile;
